import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { PermissionCheck } from "@/components";

import { PermissionRoles } from "@/enum";

import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Button } from "@/components/Button/Button";

import { WebhookTable } from "./components/WebhookTable";
import { CreateWebhookModal } from "./components/CreateWebhookModal";
import { useWebhookSigningDialogs } from "./components/signing/useWebhookSigningDialogs";

/**
 * Webhooks management surface shown on the Integrations "Webhooks" detail page. The page title and
 * description come from the surrounding IntegrationDetail header, so this renders only the actions
 * and the table.
 */
export const WebhookOverview: FunctionComponent = () => {
	const { t } = useTranslation();
	const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
	// The signing dialogs live here, outside the table: a refetch must not unmount a secret being
	// shown once (PREC-4249).
	const signing = useWebhookSigningDialogs();

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-end">
				<PermissionCheck requiredPermissions={[PermissionRoles.WEBHOOK]}>
					<Button
						color={ButtonColor.ACTION}
						size={ButtonSize.S}
						testId="create-webhook-button"
						title={t("integrations.webhooks.createCta")}
						onClick={() => setIsCreateModalOpen(true)}
					/>
				</PermissionCheck>
			</div>

			<WebhookTable signingActions={signing.actions} />

			{isCreateModalOpen && (
				<CreateWebhookModal
					isOpen={isCreateModalOpen}
					setIsOpen={setIsCreateModalOpen}
					onCreated={(webhook) => signing.actions.showCreatedSecret(webhook, webhook.signingSecret)}
				/>
			)}

			{signing.dialogs}
		</div>
	);
};
